import { EventDrivenStore } from '@core/store/eventDrivenStore';
import { computed } from 'vue';
import constants from '@core/utils/constants';
import {
  WALLET_STATUS,
  WALLET_TRANSACTION_TYPES,
  WALLET_PAYMENT_METHODS,
  WALLET_POLLING_CONFIG,
  WALLET_DEFAULT_CURRENCY
} from './constants.js';

/**
 * WalletStore V3
 * Gestion complète de l'état du wallet avec EventDrivenStore
 */
class WalletStore extends EventDrivenStore {
  constructor() {
    super('wallet', {
      // Balance state
      balance: 0,
      currency: WALLET_DEFAULT_CURRENCY,
      walletId: null,
      lastUpdated: null,

      // Transactions state
      transactions: [],
      transactionsPagination: {
        page: 1,
        limit: 20,
        total: 0,
        totalPages: 0
      },

      // Deposit state
      depositStatus: WALLET_STATUS.PENDING,
      depositTransactionId: null,
      depositFedaPayTransactionId: null,
      depositPolling: false,
      depositPollingAttempts: 0,
      depositPollingMaxAttempts: WALLET_POLLING_CONFIG.MAX_ATTEMPTS,
      depositError: null,

      // Withdrawal state
      withdrawalStatus: WALLET_STATUS.PENDING,
      withdrawalTransactionId: null,
      withdrawalPayoutId: null,
      withdrawalError: null,

      // Accounts state
      accounts: [],
      accountsLoading: false,
      accountsError: null,

      // UI state
      loading: false,
      error: null
    });

    console.log('[WalletStore] Initializing wallet store V3');
  }

  setupEvents() {
    const bus = this.bus;
    if (!bus) {
      console.warn('[WalletStore] Event bus not available');
      return;
    }

    // Balance events (compatibilité avec événements existants)
    bus.on(constants.EVENTS.WALLET.REFRESHED, (data) => {
      console.log('[WalletStore] WALLET.REFRESHED received', data);
      this.state.balance = data?.balance || 0;
      this.state.currency = data?.currency || WALLET_DEFAULT_CURRENCY;
      this.state.loading = false;
      this.state.error = null;
      this.state.lastUpdated = new Date();
    });

    bus.on(constants.EVENTS.WALLET.REFRESH_ERROR, (error) => {
      console.error('[WalletStore] WALLET.REFRESH_ERROR received', error);
      this.state.loading = false;
      this.state.error = error?.message || error;
    });

    bus.on(constants.EVENTS.WALLET.REFRESHING, () => {
      console.log('[WalletStore] WALLET.REFRESHING received');
      this.state.loading = true;
    });

    // Nouveaux événements V3 - Balance
    bus.on(constants.EVENTS.WALLET.BALANCE_UPDATE, (data) => {
      console.log('[WalletStore] WALLET.BALANCE_UPDATE received', data);
      this.state.balance = data.balance || 0;
      this.state.currency = data.currency || WALLET_DEFAULT_CURRENCY;
      this.state.walletId = data.walletId || null;
      this.state.lastUpdated = new Date();
      this.state.loading = false;
      this.state.error = null;
    });

    bus.on(constants.EVENTS.WALLET.BALANCE_ERROR, (error) => {
      console.error('[WalletStore] WALLET.BALANCE_ERROR received', error);
      this.state.loading = false;
      this.state.error = error?.message || error;
    });

    // Deposit events
    bus.on(constants.EVENTS.WALLET.DEPOSIT_POLLING_UPDATE, (data) => {
      console.log('[WalletStore] WALLET.DEPOSIT_POLLING_UPDATE received', data);
      this.state.depositStatus = data.status || WALLET_STATUS.PENDING;
      this.state.depositPollingAttempts = data.attempts || 0;

      if (data.completed) {
        this.state.depositPolling = false;
        if (data.status === WALLET_STATUS.COMPLETED) {
          // Mise à jour du solde après dépôt réussi
          this.state.balance = data.newBalance || this.state.balance;
          this.state.lastUpdated = new Date();
        }
      }
    });

    bus.on(constants.EVENTS.WALLET.DEPOSIT_ERROR, (error) => {
      console.error('[WalletStore] WALLET.DEPOSIT_ERROR received', error);
      this.state.depositPolling = false;
      this.state.depositError = error?.error || error;
      this.state.depositStatus = WALLET_STATUS.FAILED;
    });

    bus.on(constants.EVENTS.WALLET.DEPOSIT_SUCCESS, (data) => {
      console.log('[WalletStore] WALLET.DEPOSIT_SUCCESS received', data);
      this.state.depositStatus = WALLET_STATUS.COMPLETED;
      this.state.depositPolling = false;
      this.state.depositError = null;
    });

    // Withdrawal events
    bus.on(constants.EVENTS.WALLET.WITHDRAWAL_SUCCESS, (data) => {
      console.log('[WalletStore] WALLET.WITHDRAWAL_SUCCESS received', data);
      this.state.withdrawalStatus = WALLET_STATUS.COMPLETED;
      this.state.balance = data.balanceAfter || this.state.balance;
      this.state.lastUpdated = new Date();
      this.state.withdrawalError = null;
    });

    bus.on(constants.EVENTS.WALLET.WITHDRAWAL_ERROR, (error) => {
      console.error('[WalletStore] WALLET.WITHDRAWAL_ERROR received', error);
      this.state.withdrawalStatus = WALLET_STATUS.FAILED;
      this.state.withdrawalError = error?.error || error;
    });

    // Transactions events
    bus.on(constants.EVENTS.WALLET.TRANSACTIONS_UPDATE, (data) => {
      console.log('[WalletStore] WALLET.TRANSACTIONS_UPDATE received', data);
      this.state.transactions = data.transactions || [];
      if (data.pagination) {
        this.state.transactionsPagination = data.pagination;
      }
      this.state.loading = false;
      this.state.error = null;
    });

    // Accounts events
    bus.on(constants.EVENTS.WALLET.ACCOUNTS_UPDATE, (data) => {
      console.log('[WalletStore] WALLET.ACCOUNTS_UPDATE received', data);
      this.state.accounts = data.accounts || [];
      this.state.accountsLoading = false;
      this.state.accountsError = null;
    });

    bus.on(constants.EVENTS.WALLET.ACCOUNTS_ERROR, (error) => {
      console.error('[WalletStore] WALLET.ACCOUNTS_ERROR received', error);
      this.state.accountsLoading = false;
      this.state.accountsError = error?.message || error;
    });

    bus.on(constants.EVENTS.WALLET.ACCOUNT_CREATED, (data) => {
      console.log('[WalletStore] WALLET.ACCOUNT_CREATED received', data);
      if (data?.account) {
        this.state.accounts.unshift(data.account);
      }
      this.state.accountsLoading = false;
    });

    bus.on(constants.EVENTS.WALLET.ACCOUNT_CREATE_ERROR, (error) => {
      console.error('[WalletStore] WALLET.ACCOUNT_CREATE_ERROR received', error);
      this.state.accountsLoading = false;
      this.state.accountsError = error?.message || error;
    });

    console.log('[WalletStore] All event listeners set up successfully');
  }

  // Getters (computed properties)
  get getters() {
    const storeState = this.state;

    return {
      // Balance getters
      balanceFormatted: computed(() => {
        return `${storeState.balance.toLocaleString('fr-FR')} ${storeState.currency}`;
      }),

      hasBalance: computed(() => {
        return storeState.balance > 0;
      }),

      // Transaction getters
      transactionsByType: computed(() => (type) => {
        return storeState.transactions.filter(t => t.type === type);
      }),

      pendingTransactions: computed(() => {
        return storeState.transactions.filter(t => t.status === WALLET_STATUS.PENDING);
      }),

      completedTransactions: computed(() => {
        return storeState.transactions.filter(t => t.status === WALLET_STATUS.COMPLETED);
      }),

      failedTransactions: computed(() => {
        return storeState.transactions.filter(t => t.status === WALLET_STATUS.FAILED);
      }),

      creditTransactions: computed(() => {
        return storeState.transactions.filter(t => t.type === WALLET_TRANSACTION_TYPES.CREDIT);
      }),

      debitTransactions: computed(() => {
        return storeState.transactions.filter(t => t.type === WALLET_TRANSACTION_TYPES.DEBIT);
      }),

      // Deposit getters
      isDepositPending: computed(() => {
        return storeState.depositStatus === WALLET_STATUS.PENDING;
      }),

      isDepositCompleted: computed(() => {
        return storeState.depositStatus === WALLET_STATUS.COMPLETED;
      }),

      depositProgress: computed(() => {
        if (storeState.depositPollingMaxAttempts === 0) return 0;
        return Math.min(
          (storeState.depositPollingAttempts / storeState.depositPollingMaxAttempts) * 100,
          95
        );
      }),

      hasDepositError: computed(() => {
        return storeState.depositError !== null;
      }),

      // Withdrawal getters
      isWithdrawalPending: computed(() => {
        return storeState.withdrawalStatus === WALLET_STATUS.PENDING;
      }),

      isWithdrawalCompleted: computed(() => {
        return storeState.withdrawalStatus === WALLET_STATUS.COMPLETED;
      }),

      hasWithdrawalError: computed(() => {
        return storeState.withdrawalError !== null;
      }),

      // Accounts getters
      accounts: computed(() => {
        return storeState.accounts;
      }),

      accountsLoading: computed(() => {
        return storeState.accountsLoading;
      }),

      hasAccounts: computed(() => {
        return storeState.accounts && storeState.accounts.length > 0;
      }),

      // UI getters
      isLoading: computed(() => {
        return storeState.loading;
      }),

      hasError: computed(() => {
        return storeState.error !== null;
      }),

      // Computed getters
      lastUpdatedFormatted: computed(() => {
        if (!storeState.lastUpdated) return '-';
        return new Date(storeState.lastUpdated).toLocaleString('fr-FR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        });
      }),

      hasWallet: computed(() => {
        return storeState.walletId !== null;
      })
    };
  }

  // Actions (methods that mutate state)
  get actions() {
    const store = this;

    return {
      // Balance actions
      setBalance(balanceData) {
        store.state.balance = balanceData.balance || 0;
        store.state.currency = balanceData.currency || WALLET_DEFAULT_CURRENCY;
        store.state.walletId = balanceData.walletId || null;
        store.state.lastUpdated = new Date();
      },

      updateBalance(amount) {
        store.state.balance += amount;
        store.state.lastUpdated = new Date();
      },

      // Transaction actions
      setTransactions(transactionsData) {
        store.state.transactions = transactionsData.transactions || [];
        store.state.transactionsPagination = transactionsData.pagination || store.state.transactionsPagination;
      },

      addTransaction(transaction) {
        store.state.transactions.unshift(transaction);
      },

      updateTransaction(transactionId, updates) {
        const index = store.state.transactions.findIndex(t => t.id === transactionId);
        if (index !== -1) {
          store.state.transactions[index] = { ...store.state.transactions[index], ...updates };
        }
      },

      // Deposit actions
      setDepositStatus(status) {
        store.state.depositStatus = status;
      },

      setDepositTransactionId(transactionId) {
        store.state.depositTransactionId = transactionId;
      },

      setDepositFedaPayTransactionId(fedaPayTransactionId) {
        store.state.depositFedaPayTransactionId = fedaPayTransactionId;
      },

      setDepositPolling(polling) {
        store.state.depositPolling = polling;
      },

      setDepositPollingAttempts(attempts) {
        store.state.depositPollingAttempts = attempts;
      },

      setDepositError(error) {
        store.state.depositError = error;
      },

      resetDeposit() {
        store.state.depositStatus = WALLET_STATUS.PENDING;
        store.state.depositTransactionId = null;
        store.state.depositFedaPayTransactionId = null;
        store.state.depositPolling = false;
        store.state.depositPollingAttempts = 0;
        store.state.depositError = null;
      },

      // Withdrawal actions
      setWithdrawalStatus(status) {
        store.state.withdrawalStatus = status;
      },

      setWithdrawalTransactionId(transactionId) {
        store.state.withdrawalTransactionId = transactionId;
      },

      setWithdrawalPayoutId(payoutId) {
        store.state.withdrawalPayoutId = payoutId;
      },

      setWithdrawalError(error) {
        store.state.withdrawalError = error;
      },

      resetWithdrawal() {
        store.state.withdrawalStatus = WALLET_STATUS.PENDING;
        store.state.withdrawalTransactionId = null;
        store.state.withdrawalPayoutId = null;
        store.state.withdrawalError = null;
      },

      // Accounts actions
      setAccounts(accounts) {
        store.state.accounts = accounts || [];
      },

      addAccount(account) {
        store.state.accounts.unshift(account);
      },

      setAccountsLoading(loading) {
        store.state.accountsLoading = loading;
      },

      setAccountsError(error) {
        store.state.accountsError = error;
      },

      // UI actions
      setLoading(loading) {
        store.state.loading = loading;
      },

      setError(error) {
        store.state.error = error;
      },

      clearError() {
        store.state.error = null;
      },

      reset() {
        this.resetDeposit();
        this.resetWithdrawal();
        store.state.error = null;
        store.state.loading = false;
      },

      // Pagination actions
      setTransactionsPage(page) {
        store.state.transactionsPagination.page = page;
      },

      setTransactionsLimit(limit) {
        store.state.transactionsPagination.limit = limit;
      }
    };
  }
}

export default WalletStore;
